import type { InterviewSession } from '../../types';
import type { ConversationExperienceConfig } from '../../lib/multimodal-contract';
import type { FeatureKey, InterviewLanguagePolicy } from '../../lib/entitlements';
import { getCallQuestionLimitIssue } from '../../lib/call-question-limit';
import { DEFAULT_OUTPUT_LANGUAGE } from '../../lib/languages';

/** Shape of the settings form data. */
export interface ConversationSettingsValues {
  /** Maximum number of responses (null = unlimited). */
  max_responses: number | null;
  /** Whether the same email can submit multiple responses. */
  allow_duplicate_emails: boolean;
  /** Retired identity flag, cleared at save. */
  require_name: boolean;
  /** Retired identity flag, cleared at save. */
  require_email: boolean;
  /** Retired identity policy, always saved as never. */
  identity_collection: 'pre' | 'post' | 'never';
  /** Whether video recording (camera + audio) is enabled. When false, interviews are audio-only. */
  video_enabled: boolean;
  /** Whether camera is mandatory (no audio-only fallback). Only relevant when video_enabled is true. */
  require_video: boolean;
  /**
   * Whether the AI or the respondent may start a screen share mid-interview.
   * Gated by the `screen_sharing` Business entitlement (not by `video_enabled` -
   * screen sharing works on audio-only interviews too).
   */
  screen_share_enabled: boolean;
  /** Enforced language code or null for any language. */
  enforce_language: string | null;
  /** Saved call entry and permission policy. */
  conversation_config: ConversationExperienceConfig;
  /** Language for generated call content. */
  output_language: string;
  /** ISO string for when the campaign opens (null = immediately). */
  opens_at: string | null;
  /** ISO string for when the campaign closes (null = no auto-close). */
  closes_at: string | null;
  /** Submission behavior: show the success screen or redirect. */
  completion_action: 'message' | 'redirect';
  /** Custom success-screen body. NULL or blank falls back to the default. */
  completion_message: string | null;
  /** Destination URL when completion_action is 'redirect'. */
  completion_redirect_url: string | null;
}

/** Shared defaults for the builder and existing-conversation settings. */
export function buildConversationSettingsValues(session: Partial<InterviewSession> = {}): ConversationSettingsValues {
  return {
    max_responses: session.max_responses ?? null,
    allow_duplicate_emails: session.allow_duplicate_emails ?? true,
    require_name: false,
    require_email: false,
    identity_collection: 'never',
    video_enabled: session.video_enabled ?? false,
    require_video: session.require_video ?? false,
    screen_share_enabled: session.screen_share_enabled ?? false,
    enforce_language: session.enforce_language ?? null,
    // Unversioned saved conversations used calls; new drafts start in text.
    conversation_config: session.conversation_config ?? { version: 1, callsEnabled: !!session.id },
    output_language: session.output_language ?? DEFAULT_OUTPUT_LANGUAGE,
    opens_at: session.opens_at ?? null,
    closes_at: session.closes_at ?? null,
    completion_action: session.completion_action === 'redirect' ? 'redirect' : 'message',
    completion_message: session.completion_message ?? null,
    completion_redirect_url: session.completion_redirect_url ?? null,
  };
}

/** Empty custom copy uses the product default. */
function trimToNull(value: string | null): string | null {
  return value?.trim() || null;
}

/** Redirect destinations must use an ordinary web protocol. */
function isRedirectUrlValid(value: string | null): boolean {
  if (!value?.trim()) return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Context needed by the same settings guard in either save flow. */
interface SettingsValidationContext {
  /** Current authored questions, including unsaved builder edits. */
  fields: unknown;
  /** Existing responses constrain lowering the response cap. */
  responseCount: number;
  /** Resolved feature access. */
  can: (feature: FeatureKey) => boolean;
}

/** Validate settings once for both panel feedback and save admission. */
export function validateConversationSettings(value: ConversationSettingsValues, { fields, responseCount, can }: SettingsValidationContext) {
  const minResponses = Math.max(1, responseCount);
  const hasMaxResponsesError = value.max_responses !== null && (!Number.isInteger(value.max_responses) || value.max_responses < minResponses);
  const hasDateError = !!(value.opens_at && value.closes_at && new Date(value.opens_at) >= new Date(value.closes_at));
  const hasRedirectError = value.completion_action === 'redirect' && can('completion_redirect') && !isRedirectUrlValid(value.completion_redirect_url);
  const callAccessIssue = value.conversation_config.callsEnabled && value.require_video && !can('video_interviews')
    ? 'Video access is unavailable. Change the call requirement or turn calls off.'
    : value.conversation_config.callsEnabled && !can(value.video_enabled && can('video_interviews') ? 'video_interviews' : 'audio_interviews')
      ? 'Call access is unavailable. Turn calls off to continue with text.' : null;
  const callQuestionIssue = getCallQuestionLimitIssue(fields, value.conversation_config.callsEnabled);
  return { minResponses, hasMaxResponsesError, hasDateError, hasRedirectError, callAccessIssue, callQuestionIssue,
    invalid: hasMaxResponsesError || hasDateError || hasRedirectError || !!callAccessIssue || !!callQuestionIssue };
}

/** Apply the same hidden-control and entitlement rules at either save boundary. */
export function prepareConversationSettingsSave(value: ConversationSettingsValues, can: (feature: FeatureKey) => boolean, languagePolicy: InterviewLanguagePolicy): ConversationSettingsValues {
  const videoEnabled = value.conversation_config.callsEnabled && value.video_enabled && can('video_interviews');
  return {
    ...value,
    require_name: false,
    require_email: false,
    identity_collection: 'never',
    video_enabled: videoEnabled,
    require_video: videoEnabled && value.require_video,
    screen_share_enabled: value.conversation_config.callsEnabled && value.screen_share_enabled && can('screen_sharing'),
    enforce_language: languagePolicy.allowLanguageChoice ? value.enforce_language : languagePolicy.enforcedLanguage,
    completion_action: value.completion_action === 'redirect' && can('completion_redirect') ? 'redirect' : 'message',
    completion_message: trimToNull(value.completion_message),
    completion_redirect_url: trimToNull(value.completion_redirect_url),
  };
}
